import { truncate } from './logString';

export const JSON_RPC_ERROR_CODE = {
  PARSE_ERROR: -32700,
  INVALID_REQUEST: -32600,
  METHOD_NOT_FOUND: -32601,
  INVALID_PARAMS: -32602,
  INTERNAL_ERROR: -32603,
} as const;

export const APP_ERROR_CODE = {
  UNEXPECTED_ERROR: 10000,
  TARGET_NOT_READY: 10001,
  TARGET_NOT_FOUND: 10002,
  PERMISSION_DENIED: 10003,
  OPERATION_REJECTED: 10004,
  STATE_CONFLICT: 10005,
  METHOD_EXECUTION_FAILED: 10006,
} as const;

// A: 同じリクエストをそのまま再送して直る見込みがある
// B: caller のリクエスト自体を変えないと直らない
// C: 再送でもリクエスト変更でも直らない、または判断できない
type RecoveryAction =
  | 'open_target_and_retry' // A
  | 'reload_and_retry' // A
  | 'check_input' // B
  | 'check_permission' // A
  | 'retry_later' // A
  | 'fix_request' // B
  | 'cannot_recover' // C
  | 'unknown'; // C

type JsonRpcErrorData = {
  reason: string;
  detail: string;
  recovery: RecoveryAction;
  guidance: string;
};

type JsonRpcStandardErrorCode = (typeof JSON_RPC_ERROR_CODE)[keyof typeof JSON_RPC_ERROR_CODE];
type JsonRpcAppErrorCode = (typeof APP_ERROR_CODE)[keyof typeof APP_ERROR_CODE];

// wire 上の error オブジェクトに kind は含まない（シリアライズ時に除外）
type StandardError = {
  kind: 'standard';
  code: JsonRpcStandardErrorCode;
  message: string;
  data: JsonRpcErrorData;
};

export type ApplicationError = {
  kind: 'application';
  code: JsonRpcAppErrorCode;
  message: string;
  data: JsonRpcErrorData;
};

export type JsonRpcError = StandardError | ApplicationError;

const STANDARD_ERROR_DEFAULTS: Record<
  JsonRpcStandardErrorCode,
  { message: string; recovery: RecoveryAction; guidance: string }
> = {
  [JSON_RPC_ERROR_CODE.PARSE_ERROR]: {
    message: 'Parse error',
    recovery: 'fix_request',
    guidance: 'Fix the request format on the sender side and send a new request.',
  },
  [JSON_RPC_ERROR_CODE.INVALID_REQUEST]: {
    message: 'Invalid Request',
    recovery: 'fix_request',
    guidance: 'Fix the request format on the sender side and send a new request.',
  },
  [JSON_RPC_ERROR_CODE.METHOD_NOT_FOUND]: {
    message: 'Method not found',
    recovery: 'fix_request',
    guidance: 'Fix the request format on the sender side and send a new request.',
  },
  [JSON_RPC_ERROR_CODE.INVALID_PARAMS]: {
    message: 'Invalid params',
    recovery: 'fix_request',
    guidance: 'Fix the request parameters on the sender side and send a new request.',
  },
  [JSON_RPC_ERROR_CODE.INTERNAL_ERROR]: {
    message: 'Internal error',
    recovery: 'retry_later',
    guidance: 'Retry the request after a moment.',
  },
};

function buildStandardError(code: JsonRpcStandardErrorCode, reason: string, detail: string): StandardError {
  const defaults = STANDARD_ERROR_DEFAULTS[code];
  return {
    kind: 'standard',
    code,
    message: defaults.message,
    data: { reason, detail: truncate(detail), recovery: defaults.recovery, guidance: defaults.guidance },
  };
}

export function parseError(reason: string, detail: string): StandardError {
  return buildStandardError(JSON_RPC_ERROR_CODE.PARSE_ERROR, reason, detail);
}

export function invalidRequestError(reason: string, detail: string): StandardError {
  return buildStandardError(JSON_RPC_ERROR_CODE.INVALID_REQUEST, reason, detail);
}

export function invalidParamsError(reason: string, detail: string): StandardError {
  return buildStandardError(JSON_RPC_ERROR_CODE.INVALID_PARAMS, reason, detail);
}

export function methodNotFoundError(reason: string, detail: string): StandardError {
  return buildStandardError(JSON_RPC_ERROR_CODE.METHOD_NOT_FOUND, reason, detail);
}
